// src/pages/ProductPage.jsx
import React, { useState, useEffect, useContext, useCallback } from "react";
import { TranslatorContext } from "../../../context/Translator";
import { Link } from "react-router-dom";
import { Row, Col, Form } from "react-bootstrap";
import Select from "react-select";
import { FloatCardComponent } from "../../../components/cards";
import { ButtonComponent } from "../../../components/elements";
import ProductListComponent from "../../../components/tables/Admin/ProductsListComponent";
import PageLayout from "../../../layouts/PageLayout";
import axios from "axios";
import Swal from 'sweetalert2';

export default function ProductPage() {
    const { t } = useContext(TranslatorContext);
    const AuthToken = localStorage.getItem("authToken");

    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchInput, setSearchInput] = useState(''); // For immediate input display
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0
    });
    
    // Filter states
    const [filters, setFilters] = useState({
        name: '',
        cat_id: '',
        vendor_id: ''
    });
    
    const floats = [
        { title: "total_products", digit: pagination.total, icon: "shopping_bag", variant: "lg blue" },
        { title: "total_categories", digit: categories.length, icon: "widgets", variant: "lg green" },
        { title: "total_brands", digit: 249, icon: "verified_user", variant: "lg purple" }
    ];

    const fetchProducts = async (page = 1) => {
        try {
            setLoading(true);
            axios.defaults.headers.common["Authorization"] = `Bearer ${AuthToken}`;
            
            const params = new URLSearchParams({
                page: page,
                per_page: pagination.per_page,
                ...(filters.name && { name: filters.name }),
                ...(filters.cat_id && { cat_id: filters.cat_id }),
                ...(filters.vendor_id && { vendor_id: filters.vendor_id })
            });
            
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/products?${params}`
            );
            
            setProducts(res.data.products.data || []);
            setCategories(res.data.categories || []);
            setPagination({
                current_page: res.data.products.current_page,
                last_page: res.data.products.last_page,
                per_page: res.data.products.per_page,
                total: res.data.products.total
            });
        } catch (err) {
            console.error("Fetch Error:", err);
            Swal.fire({
                icon: 'error',
                title: t('error'),
                text: t('Failed to fetch products'),
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true
            });
        } finally {
            setLoading(false);
        }
    };

    // Debounce function
    const debounce = (func, delay) => {
        let timeoutId;
        return (...args) => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                func(...args);
            }, delay);
        };
    };

    // Handle search with debounce - 800ms delay
    const debouncedSearch = useCallback(
        debounce((value) => {
            setFilters(prev => ({ ...prev, name: value }));
        }, 800),
        []
    );

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchInput(value); // Update input immediately
        debouncedSearch(value); // Trigger search after delay
    };

    useEffect(() => {
        fetchProducts(1);
    }, [filters.name, filters.cat_id, filters.vendor_id, pagination.per_page]);

    useEffect(() => {
        const fetchVendors = async () => {
            try {
                axios.defaults.headers.common["Authorization"] = `Bearer ${AuthToken}`;
                const params = new URLSearchParams({ page: 1, per_page: 1000 });
                const res = await axios.get(`${process.env.REACT_APP_API_URL}/vendors?${params}`);
                let vendorList = [];
                if (Array.isArray(res.data.data)) vendorList = res.data.data;
                else if (Array.isArray(res.data.vendors)) vendorList = res.data.vendors;
                else if (Array.isArray(res.data)) vendorList = res.data;
                setVendors(vendorList);
            } catch (err) {
                console.error("Failed to fetch vendors:", err);
            }
        };

        if (AuthToken) fetchVendors();
    }, [AuthToken]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        if (name === 'name') {
            setSearchInput(value);
            debouncedSearch(value);
        } else {
            setFilters(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleVendorChange = (selected) => {
        setFilters(prev => ({ ...prev, vendor_id: selected ? selected.value : '' }));
    };

    const handlePageChange = (page) => {
        fetchProducts(page);
    };

    const handlePerPageChange = (e) => {
        setPagination(prev => ({ ...prev, per_page: parseInt(e.target.value) }));
    };

    const clearFilters = () => {
        setSearchInput('');
        setFilters({ name: '', cat_id: '', vendor_id: '' });
    };

    // Handle product delete
    const handleDeleteProduct = async (id, productName) => {
        Swal.fire({
            title: t('Are you sure?'),
            text: t(`Want to delete product "${productName}"?`),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('delete'),
            cancelButtonText: t('cancel')
        }).then(async (result) => {
            if (result.isConfirmed) {
                Swal.fire({
                    title: t('Deleting...'),
                    text: t('Please wait'),
                    allowOutsideClick: false,
                    didOpen: () => {
                        Swal.showLoading();
                    }
                });

                try {
                    await axios.delete(
                        `${process.env.REACT_APP_API_URL}/products-delete/${id}`,
                        {
                            headers: { Authorization: `Bearer ${AuthToken}` }
                        }
                    );
                    
                    Swal.close();
                    
                    Swal.fire({
                        icon: 'success',
                        title: t('deleted'),
                        text: t(`Product "${productName}" deleted successfully`),
                        toast: true,
                        position: 'top-end',
                        showConfirmButton: false,
                        timer: 3000,
                        timerProgressBar: true
                    });
                    
                    fetchProducts(pagination.current_page);
                    
                } catch (error) {
                    Swal.close();
                    console.error('Error deleting product:', error);
                    
                    Swal.fire({
                        icon: 'error',
                        title: t('error'),
                        text: error.response?.data?.message || t('Failed to delete product'),
                        toast: true,
                        position: 'top-end',
                        showConfirmButton: false,
                        timer: 3000,
                        timerProgressBar: true
                    });
                }
            }
        });
    };

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card">
                        <div className="mc-breadcrumb">
                            <h3 className="mc-breadcrumb-title">{t('product_list')}</h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link">{t('home')}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link">{t('products')}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">{t('product_list')}</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                {/* Filters Section */}
                <Col xl={12}>
                    <div className="mc-card p-3 mb-3">
                        <Row className="align-items-end">
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>{t('Search by name')}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="name"
                                        value={searchInput}
                                        onChange={handleSearchChange}
                                        placeholder={t('Enter product name...')}
                                    />
                                    
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label>{t('Vendor')}</Form.Label>
                                    <Select
                                        name="vendor_id"
                                        value={vendors
                                            .filter(vendor => vendor.id === filters.vendor_id)
                                            .map(vendor => ({ value: vendor.id, label: vendor.name }))[0] || null
                                        }
                                        options={vendors.map(vendor => ({ value: vendor.id, label: vendor.name }))}
                                        onChange={handleVendorChange}
                                        isClearable
                                        placeholder={t('Search vendor...')}
                                        styles={{
                                            control: (provided) => ({ ...provided, minHeight: '38px' }),
                                            menu: (provided) => ({ ...provided, zIndex: 9999 }),
                                        }}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label>{t('Category')}</Form.Label>
                                    <Form.Select
                                        name="cat_id"
                                        value={filters.cat_id}
                                        onChange={handleFilterChange}
                                    >
                                        <option value="">{t('All Categories')}</option>
                                        {categories.map(cat => (
                                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                           
                            <Col md={2}>
                                <ButtonComponent
                                    type="button"
                                    className="btn btn-secondary w-100"
                                    onClick={clearFilters}
                                >
                                    {t('Clear Filters')}
                                </ButtonComponent>
                            </Col>
                        </Row>
                    </div>
                </Col>

                {/* Products Table */}
                <Col xl={12}>
                    <div className="mc-card">
                        <Row>
                            <Col xl={12}>
                                {!loading ? (
                                    <ProductListComponent 
                                        products={products}
                                        onDeleteProduct={handleDeleteProduct}
                                        pagination={pagination}
                                        onPageChange={handlePageChange}
                                    />
                                ) : (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">{t('loading')}...</span>
                                        </div>
                                        <p className="mt-2">{t('loading')}...</p>
                                    </div>
                                )}
                            </Col>
                        </Row>
                    </div>
                </Col>
            </Row>
        </PageLayout>
    );
}